import { Activity, Ban, CheckCircle2, CircleDashed, CircleDot, type LucideProps, TriangleAlert } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Badge, type BadgeTone } from './surface';

/**
 * Availability status semantics (brief section 60).
 * Every status is communicated with colour AND icon AND text - never colour alone.
 */
export type AvailabilityStatus = 'available' | 'available_with_restrictions' | 'rehabilitation' | 'unavailable';

export interface AvailabilityMeta {
  label: string;
  /** Short label for tight spaces. */
  short: string;
  tone: BadgeTone;
  icon: ComponentType<LucideProps>;
  /** Bar / dot fill for charts and stacked summaries (>= 3:1 on surface). */
  solid: string;
  description: string;
}

export const AVAILABILITY_META: Record<AvailabilityStatus, AvailabilityMeta> = {
  available: {
    label: 'Available',
    short: 'Available',
    tone: 'success',
    icon: CheckCircle2,
    solid: 'var(--success-solid)',
    description: 'Cleared for full training and matches',
  },
  available_with_restrictions: {
    label: 'Restricted',
    short: 'Restricted',
    tone: 'warning',
    icon: TriangleAlert,
    solid: 'var(--warning-solid)',
    description: 'Can take part with limits set by the physiotherapist',
  },
  rehabilitation: {
    label: 'Rehabilitation',
    short: 'Rehab',
    tone: 'rehab',
    icon: Activity,
    solid: 'var(--rehab-solid)',
    description: 'Following a rehabilitation programme',
  },
  unavailable: {
    label: 'Unavailable',
    short: 'Unavailable',
    tone: 'danger',
    icon: Ban,
    solid: 'var(--danger-solid)',
    description: 'Not to train or play',
  },
};

export const AVAILABILITY_ORDER: AvailabilityStatus[] = ['available', 'available_with_restrictions', 'rehabilitation', 'unavailable'];

export function AvailabilityBadge({
  status,
  size = 'md',
  compact,
  className,
}: {
  status: AvailabilityStatus;
  size?: 'sm' | 'md';
  /** Use the short label. */
  compact?: boolean;
  className?: string;
}) {
  const meta = AVAILABILITY_META[status];
  const Icon = meta.icon;
  return (
    <Badge tone={meta.tone} size={size} icon={<Icon aria-hidden />} className={className}>
      {compact ? meta.short : meta.label}
    </Badge>
  );
}

export type InjurySeverity = 'mild' | 'moderate' | 'severe';

const SEVERITY: Record<InjurySeverity, { label: string; tone: BadgeTone }> = {
  mild: { label: 'Mild', tone: 'info' },
  moderate: { label: 'Moderate', tone: 'warning' },
  severe: { label: 'Severe', tone: 'danger' },
};

export function SeverityBadge({ severity, size = 'md' }: { severity: InjurySeverity; size?: 'sm' | 'md' }) {
  const s = SEVERITY[severity];
  return (
    <Badge tone={s.tone} size={size}>
      {s.label}
    </Badge>
  );
}

/** Workflow state badge. Tone comes from the state's category (configurable per organization). */
export type WorkflowCategory = 'open' | 'active' | 'terminal';

export function WorkflowStateBadge({ label, category, size = 'md' }: { label: string; category: WorkflowCategory; size?: 'sm' | 'md' }) {
  // Deliberately not the brand tone: with a red-ish brand colour a "brand" badge reads as an error.
  const meta = {
    open: { tone: 'info' as const, icon: <CircleDashed aria-hidden /> },
    active: { tone: 'neutral' as const, icon: <CircleDot aria-hidden /> },
    terminal: { tone: 'success' as const, icon: <CheckCircle2 aria-hidden /> },
  }[category];
  return (
    <Badge tone={meta.tone} size={size} icon={meta.icon}>
      {label}
    </Badge>
  );
}

/** Small coloured dot + label, for dense lists and legends. */
export function StatusDot({ status, children, className }: { status: AvailabilityStatus; children?: ReactNode; className?: string }) {
  const meta = AVAILABILITY_META[status];
  return (
    <span className={cn('inline-flex items-center gap-2 text-sm text-fg', className)}>
      <span aria-hidden className="size-2.5 rounded-full" style={{ background: meta.solid }} />
      {children ?? meta.label}
    </span>
  );
}
